export type PublisherAgreementSection = {
  number: number;
  title: string;
  paragraphs: string[];
  listItems?: string[];
};

export const PUBLISHER_AGREEMENT_LAST_UPDATED = "9th August 2026";

export const publisherAgreementSections: PublisherAgreementSection[] = [
  {
    number: 1,
    title: "Platform Role and Status",
    paragraphs: [
      "OpenAppCart provides a technology platform that enables publishers to list, distribute and manage Android application files and related listing information.",
      "OpenAppCart is not the developer, owner, publisher, seller, distributor, partner, agent or representative of any app listed on the platform. OpenAppCart does not develop, endorse, guarantee, control or assume responsibility for publisher apps or their operation.",
      "The relationship between OpenAppCart and a publisher is that of an independent platform provider and an independent publisher. Nothing in this Agreement creates employment, partnership, joint venture or agency between the parties.",
    ],
  },
  {
    number: 2,
    title: "Publisher Eligibility and Authority",
    paragraphs: [
      "By using publisher services, you confirm that you are legally able to enter this Agreement and that all registration information you provide is accurate and kept current.",
      "If you act for a company or other entity, you confirm that you have authority to bind that entity. The entity will be treated as the publisher and will remain responsible for all activity under its account.",
    ],
  },
  {
    number: 3,
    title: "Ownership, Licences and Intellectual Property",
    paragraphs: [
      "You confirm that you own, or have all licences, permissions, consents and rights necessary to publish, distribute, promote and make available each app and all materials included in its listing.",
      "You grant OpenAppCart a non-exclusive, worldwide, royalty-free licence to host, store, reproduce, display, distribute and otherwise use app files and listing materials solely to operate, promote and secure the platform, subject to applicable law and platform settings.",
      "You must not upload material that infringes copyright, trademark, patent, trade secret or other intellectual property rights, or that you are not authorised to make available.",
    ],
  },
  {
    number: 4,
    title: "Publisher Responsibility for App Content",
    paragraphs: [
      "You are solely responsible for each app you publish, including its code, content, functionality, claims, descriptions, screenshots, pricing information, support, updates and all user-facing materials.",
      "You are responsible for ensuring that each app complies with applicable laws, regulations, app-store-equivalent standards, consumer protection requirements and OpenAppCart policies.",
      "Any dispute, complaint, claim or liability relating to an app rests primarily with the publisher, not OpenAppCart.",
    ],
  },
  {
    number: 5,
    title: "Prohibited Apps and Prohibited Conduct",
    paragraphs: ["You must not publish, promote or distribute through OpenAppCart:"],
    listItems: [
      "illegal, fraudulent, deceptive, misleading or unauthorised material;",
      "content that infringes intellectual property or other legal rights;",
      "apps designed primarily to harm users, devices, networks or third parties;",
      "material that promotes unlawful discrimination, harassment, exploitation or violence;",
      "spyware, stalkerware or apps that covertly collect data or bypass user consent;",
      "material that violates privacy, security or platform integrity requirements;",
      "any app or conduct that could expose OpenAppCart or users to legal, regulatory or reputational risk.",
    ],
  },
  {
    number: 6,
    title: "Malware, Security and Harmful Code",
    paragraphs: [
      "You must not upload malicious, deceptive, harmful, compromised or unauthorised software, code, scripts, permissions, updates or related files.",
      "You remain fully responsible for your app, its code, permissions, security, updates, conduct, support and all resulting user impact, even where OpenAppCart applies automated or manual review measures.",
      "OpenAppCart may use automated and third-party security scanning technologies to inspect submitted applications for known or suspected malware, spyware, malicious code, suspicious permissions, harmful payloads and other security indicators. Uploaded apps may be scanned before publication and, where technically possible, periodically re-scanned after publication. Security scanning reduces risk but cannot guarantee detection of every threat, and OpenAppCart does not guarantee that any application is clean, complete, or free from error in security scanning.",
      "Where security systems or reasonable review indicate risk, OpenAppCart may reject, quarantine, suspend, remove or disable distribution of an application, including blocking publication, removing the app from public discovery or download access, preserving the relevant security event or log for review, notifying the publisher and investigating further action.",
      "OpenAppCart may act without prior notice where immediate action is reasonably necessary to protect users, the platform, infrastructure, third parties or comply with law.",
      "Automated scanning is an additional platform-protection measure only. It does not guarantee that every vulnerability, threat or malicious component will be identified, and it does not transfer legal responsibility from the publisher to OpenAppCart.",
    ],
  },
  {
    number: 7,
    title: "Privacy, Data Protection and User Permissions",
    paragraphs: [
      "You are solely responsible for personal data processed by your app, including collection, use, storage, sharing, retention, security, notices, consents and user rights.",
      "You must request and use device permissions lawfully, transparently and only where necessary for stated app functionality.",
      "You must maintain a clear privacy notice for your app and comply with applicable data protection and consumer privacy laws.",
    ],
  },
  {
    number: 8,
    title: "Third-Party Services, Payments and Transactions",
    paragraphs: [
      "Your app may integrate third-party services, payment providers, analytics tools, ads, APIs or external websites. You remain responsible for those integrations and for all resulting obligations.",
      "OpenAppCart does not process end-user commercial transactions on your behalf unless expressly stated in a separate written agreement.",
      "You remain responsible for taxes, refunds, chargebacks, customer terms, billing disputes and compliance with payment, e-commerce and consumer laws connected to your app.",
    ],
  },
  {
    number: 9,
    title: "App Accuracy, Updates and Ongoing Maintenance",
    paragraphs: [
      "You must ensure that listing information is accurate, current and not misleading, including app name, description, version, permissions, screenshots and availability.",
      "You must promptly update, patch or remove apps that are outdated, unsafe, non-compliant, defective or no longer supported.",
      "Failure to maintain an app may result in suspension, downgrade, delisting or removal where reasonably necessary for user protection or platform integrity.",
    ],
  },
  {
    number: 10,
    title: "Reviews, Ratings, Downloads and Platform Metrics",
    paragraphs: [
      "OpenAppCart may display downloads, ratings, reviews, rankings, visibility metrics or similar platform data.",
      "OpenAppCart does not guarantee any minimum downloads, ranking, visibility, rating, review volume, availability, uptime, performance or commercial outcome.",
      "You must not manipulate metrics through fake downloads, fake reviews, incentive abuse, bots or other deceptive practices.",
    ],
  },
  {
    number: 11,
    title: "Suspension, Removal and Termination",
    paragraphs: [
      "OpenAppCart may reject, suspend, disable, restrict or remove any app or publisher account where reasonably necessary for legal, safety, security, policy, fraud-prevention or platform-protection reasons.",
      "OpenAppCart may take such action with or without notice where urgent risk or legal obligation requires it.",
      "You may stop using publisher services at any time, but remain responsible for apps already distributed, user obligations, data handling and liabilities arising before or after termination, to the extent permitted by law.",
    ],
  },
  {
    number: 12,
    title: "Complaints, Takedown Requests and Legal Cooperation",
    paragraphs: [
      "OpenAppCart may review complaints, rights-holder notices, regulatory enquiries and lawful requests relating to published apps.",
      "You must respond promptly and cooperate with reasonable information requests connected to your app, including ownership, licensing, content, security and compliance matters.",
      "OpenAppCart may disclose information and take appropriate action in response to lawful government, court, regulatory or rights-holder requests, subject to applicable law.",
    ],
  },
  {
    number: 13,
    title: "Disclaimers and No Warranty",
    paragraphs: [
      "The platform and publisher services are provided on an \"as is\" and \"as available\" basis, to the extent permitted by applicable law.",
      "OpenAppCart does not warrant uninterrupted access, error-free operation, compatibility with every device, or that every app or listing is accurate, lawful, secure or free from harmful code.",
      "Users download and use publisher apps at their own risk. OpenAppCart is not responsible for publisher apps or third-party services linked from them.",
    ],
  },
  {
    number: 14,
    title: "Limitation of Liability and Indemnity",
    paragraphs: [
      "To the extent permitted by applicable law, OpenAppCart will not be liable for indirect, incidental, special, consequential or punitive loss, or for loss of profits, revenue, data, goodwill or business opportunity arising from publisher apps or use of the platform.",
      "Where liability cannot be excluded, OpenAppCart's aggregate liability arising from publisher services will be limited to the amount paid by you to OpenAppCart for the relevant app membership in the twelve months before the event giving rise to the claim, unless applicable law requires otherwise.",
      "You agree to indemnify and hold harmless OpenAppCart against claims, losses, liabilities, damages, costs and proceedings arising from your app, content, conduct, infringement, privacy practices, security issues, misrepresentation or legal non-compliance, to the extent permitted by applicable law.",
    ],
  },
  {
    number: 15,
    title: "Membership, Fees, Renewal and Account Responsibility",
    paragraphs: [
      "Publisher membership fees, renewals and related charges are disclosed during registration or checkout. Payment of fees does not guarantee approval, listing, continued publication, commercial success or immunity from removal where legal or policy issues arise.",
      "You are responsible for maintaining valid payment details, renewing memberships on time and all activity under your account, including actions by authorised users.",
      "Failure to pay applicable fees may result in suspension, non-renewal, delisting or loss of publisher access, subject to applicable law and platform policy.",
    ],
  },
  {
    number: 16,
    title: "Governing Law, Disputes, Changes and Acceptance",
    paragraphs: [
      "This Agreement is governed by the laws applicable in the jurisdiction specified by OpenAppCart in its platform terms or registration flow, subject to mandatory consumer or local law rights that cannot be excluded.",
      "OpenAppCart may update this Agreement by publishing a revised version on the platform. Material changes may require renewed acceptance before further publishing activity.",
      "Acceptance occurs when you check the acceptance box during publishing and/or continue using publisher services after an updated agreement is presented, whichever applies first.",
      "If you do not agree to an updated Agreement, you must stop publishing and using publisher services and remove or disable affected apps as required.",
    ],
  },
];
